import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './Dashboard.css';
import Logo from '../../assets/ernify-logo.png';
import GooglePlayStoreIcon from '../../assets/google-play-store.png';
import HeroCommunity from '../../assets/hero-community-v1.png';

const playStoreUrl = 'https://play.google.com/store/apps/details?id=com.ernify';

const Icon = ({ name, size = 24, className = '' }) => {
  const props = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: '1.9',
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': true,
    className,
  };

  const icons = {
    menu: (
      <>
        <path d="M4 6h16" />
        <path d="M4 12h16" />
        <path d="M4 18h16" />
      </>
    ),

    close: (
      <>
        <path d="m6 6 12 12" />
        <path d="m18 6-12 12" />
      </>
    ),

    arrow: (
      <>
        <path d="M5 12h14" />
        <path d="m13 6 6 6-6 6" />
      </>
    ),

    chevron: <path d="m9 18 6-6-6-6" />,

    play: (
      <path
        d="m9 7 8 5-8 5V7Z"
        fill="currentColor"
        stroke="none"
      />
    ),

    game: (
      <>
        <rect x="3" y="7" width="18" height="11" rx="4" />
        <path d="M8 10v5M5.5 12.5h5M16.5 11h.01M18.5 13h.01" />
      </>
    ),

    bolt: <path d="m13 2-8 12h6l-1 8 9-13h-6l1-7Z" />,

    wallet: (
      <>
        <path d="M4 7.5A2.5 2.5 0 0 1 6.5 5H18a2 2 0 0 1 2 2v11H6a2 2 0 0 1-2-2V7.5Z" />
        <path d="M4 8h14" />
        <path d="M16 13h4" />
        <path d="M16 13h.01" />
      </>
    ),

    gift: (
      <>
        <rect x="3" y="8" width="18" height="12" rx="2" />
        <path d="M12 8v12M3 12h18M12 8H8.5a2.25 2.25 0 1 1 2.25-2.25C10.75 7 12 8 12 8Zm0 0h3.5a2.25 2.25 0 1 0-2.25-2.25C13.25 7 12 8 12 8Z" />
      </>
    ),

    calendar: (
      <>
        <rect x="3.5" y="5.5" width="17" height="15" rx="2" />
        <path d="M7.5 3.5v4M16.5 3.5v4M3.5 10h17M8 14h.01M12 14h.01M16 14h.01M8 17h.01M12 17h.01" />
      </>
    ),

    shield: (
      <>
        <path d="M12 3 20 6v5c0 5-3.2 8.4-8 10-4.8-1.6-8-5-8-10V6l8-3Z" />
        <path d="m8.5 12 2.2 2.2 4.8-4.8" />
      </>
    ),

    check: <path d="m5 12 4.2 4.2L19 6.5" />,

    users: (
      <>
        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
      </>
    ),

    bank: (
      <>
        <path d="M3 10h18" />
        <path d="m5 10 7-5 7 5" />
        <path d="M5 10v8M9 10v8M15 10v8M19 10v8M3 18h18M2 21h20" />
      </>
    ),
  };

  return <svg {...props}>{icons[name]}</svg>;
};

const StoreBadge = ({ className = '' }) => (
  <a
    className={`store-badge ${className}`}
    href={playStoreUrl}
    target="_blank"
    rel="noopener noreferrer"
    aria-label="Download Ernify on Google Play"
  >
    <img
      src={GooglePlayStoreIcon}
      alt="Get it on Google Play"
    />
  </a>
);

const games = [
  {
    key: 'tic-tac-toe',
    title: 'Tic Tac Toe',
    description: 'Classic fun every time',
    accent: 'mint',
  },
  {
    key: 'memory-match',
    title: 'Memory Match',
    description: 'Test your memory skills',
    accent: 'violet',
  },
  {
    key: 'bubble-shoot',
    title: 'Bubble Shoot',
    description: 'Aim, shoot and score',
    accent: 'orange',
  },
  {
    key: 'number-blitz',
    title: 'Number Blitz',
    description: 'Quick math, big rewards',
    accent: 'blue',
  },
  {
    key: 'snake-game',
    title: 'Snake Game',
    description: 'The retro arcade challenge',
    accent: 'teal',
  },
  {
    key: 'flappy-clone',
    title: 'Flappy Clone',
    description: 'A challenge of perfect timing',
    accent: 'sky',
  },
  {
    key: 'tap-sprint',
    title: 'Tap Sprint',
    description: 'Hit the moving target',
    accent: 'gold',
  },
  {
    key: 'word-scramble',
    title: 'Word Scramble',
    description: 'Solve quick word puzzles',
    accent: 'lilac',
  },
  {
    key: 'maze-escape',
    title: 'Maze Escape',
    description: 'Find the route to freedom',
    accent: 'cyan',
  },
  {
    key: 'reaction-rush',
    title: 'Reaction Rush',
    description: 'React only when it is green',
    accent: 'pink',
  },
];

const GameArt = ({ name }) => {
  if (name === 'tic-tac-toe') {
    return (
      <div className="game-art tic-tac-art" aria-hidden="true">
        <span>×</span>
        <span>○</span>
        <span>○</span>
        <span>×</span>
      </div>
    );
  }

  if (name === 'memory-match') {
    return (
      <div className="game-art memory-art" aria-hidden="true">
        <span>✦</span>
        <span>✦</span>
      </div>
    );
  }

  if (name === 'bubble-shoot') {
    return (
      <div className="game-art bubble-art" aria-hidden="true">
        <span />
        <span />
        <span />
        <span />
      </div>
    );
  }

  if (name === 'number-blitz') {
    return (
      <div className="game-art number-art" aria-hidden="true">
        <span>1</span>
        <span>2</span>
        <span>3</span>
      </div>
    );
  }

  if (name === 'snake-game') {
    return (
      <div className="game-art snake-art" aria-hidden="true">
        <i />
        <i />
        <i />
        <i />
        <b />
      </div>
    );
  }

  if (name === 'flappy-clone') {
    return (
      <div className="game-art flappy-art" aria-hidden="true">
        <span>◒</span>
        <i />
        <i />
      </div>
    );
  }

  if (name === 'tap-sprint') {
    return (
      <div className="game-art tap-art" aria-hidden="true">
        <span>✦</span>
        <i />
      </div>
    );
  }

  if (name === 'word-scramble') {
    return (
      <div className="game-art word-art" aria-hidden="true">
        <span>A</span>
        <span>B</span>
        <span>C</span>
      </div>
    );
  }

  if (name === 'maze-escape') {
    return (
      <div className="game-art maze-art" aria-hidden="true">
        <span />
        <span />
        <span />
      </div>
    );
  }

  return (
    <div className="game-art reaction-art" aria-hidden="true">
      <span>⚡</span>
      <i />
    </div>
  );
};

const Dashboard = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const closeMenu = () => setIsMenuOpen(false);

  return (
    <div className="dashboard-container dark-theme">
      <a className="skip-link" href="#main-content">
        Skip to content
      </a>

      <header className="dashboard-header">
        <Link
          to="/"
          className="logo-container"
          aria-label="Ernify home"
          onClick={closeMenu}
        >
          <img
            src={Logo}
            className="app-logo"
            alt="Ernify"
          />
        </Link>

        <button
          className="menu-toggle"
          type="button"
          onClick={() => setIsMenuOpen((open) => !open)}
          aria-expanded={isMenuOpen}
          aria-controls="main-navigation"
          aria-label={
            isMenuOpen
              ? 'Close navigation menu'
              : 'Open navigation menu'
          }
        >
          <Icon
            name={isMenuOpen ? 'close' : 'menu'}
            size={25}
          />
        </button>

        <nav
          id="main-navigation"
          className={`main-nav ${isMenuOpen ? 'open' : ''}`}
          aria-label="Main navigation"
        >
          <a href="#how-it-works" onClick={closeMenu}>
            How it works
          </a>

          <a href="#rewards" onClick={closeMenu}>
            Rewards
          </a>

          <a href="#games" onClick={closeMenu}>
            Games
          </a>

          <a href="#faq" onClick={closeMenu}>
            FAQ
          </a>

          <Link to="/about-us" onClick={closeMenu}>
            About
          </Link>

          <Link
            className="mobile-only-link"
            to="/contact-us"
            onClick={closeMenu}
          >
            Support
          </Link>
        </nav>

        <a
          className="header-download"
          href={playStoreUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          Get the App
          <Icon name="arrow" size={17} />
        </a>
      </header>

      <main id="main-content">
        <section className="hero-section hero-section-v2">
          <div
            className="hero-ambient hero-ambient-one"
            aria-hidden="true"
          />

          <div
            className="hero-ambient hero-ambient-two"
            aria-hidden="true"
          />

          <div className="hero-main">
            <div className="hero-content hero-content-v2">
              <div className="eyebrow">
                <span className="eyebrow-pulse" />
                Play today. A brighter tomorrow.
              </div>

              <h1>
                Turn your play time
                <span>into real rewards.</span>
              </h1>

              <p className="hero-subtitle">
                Play fun mini games, collect Ernify Coins,
                claim daily bonuses and redeem eligible
                rewards — all in one simple app.
              </p>

              <div className="hero-actions hero-actions-v2">
                <a
                  className="hero-download-card"
                  href={playStoreUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span className="hero-play-mark">
                    <Icon name="play" size={24} />
                  </span>

                  <span className="hero-cta-copy">
                    <b>Get the App</b>
                    <small>Free on Google Play</small>
                  </span>

                  <Icon name="arrow" size={23} />
                </a>

                <a
                  className="how-it-works-card"
                  href="#how-it-works"
                >
                  <span className="how-play-mark">
                    <Icon name="play" size={17} />
                  </span>

                  <span className="hero-cta-copy">
                    <b>How it Works</b>
                    <small>See reward rules</small>
                  </span>
                </a>
              </div>

              <div
                className="hero-footnotes hero-footnotes-v2"
                aria-label="Ernify key benefits"
              >
                <span>
                  <Icon name="check" size={16} />
                  Daily bonus
                </span>

                <span>
                  <Icon name="check" size={16} />
                  Fun mini games
                </span>

                <span>
                  <Icon name="check" size={16} />
                  Clear reward rules
                </span>
              </div>

              <div className="hero-social-proof">
                <div className="hero-proof-icon">
                  <Icon name="users" size={22} />
                </div>

                <div className="proof-copy">
                  <strong>Made for every generation</strong>
                  <span>
                    Simple to play. Easy to understand.
                  </span>
                </div>

                <div className="hero-conversion-pill">
                  <span className="coin-mini">₹</span>
                  10 Coins = ₹1
                </div>
              </div>
            </div>

            <div
              className="hero-visual hero-visual-v2"
              aria-label="Ernify community players from different generations using the app"
            >
              <div
                className="hero-visual-ring ring-one"
                aria-hidden="true"
              />

              <div
                className="hero-visual-ring ring-two"
                aria-hidden="true"
              />

              <div
                className="hero-visual-glow"
                aria-hidden="true"
              />

              <img
                className="hero-community-image"
                src={HeroCommunity}
                alt="Ernify users from different generations enjoying the app on their phones"
              />

              <div className="hero-float-card hero-float-daily">
                <span className="hero-float-icon mint">
                  <Icon name="calendar" size={18} />
                </span>

                <div>
                  <b>Daily Bonus</b>
                  <small>Claim &amp; build your streak</small>
                </div>
              </div>

              <div className="hero-float-card hero-float-games">
                <span className="hero-float-icon blue">
                  <Icon name="game" size={18} />
                </span>

                <div>
                  <b>Play &amp; Earn</b>
                  <small>
                    Short games, visible progress
                  </small>
                </div>
              </div>

              <div className="hero-float-card hero-float-withdraw">
                <span className="hero-float-icon violet">
                  <Icon name="wallet" size={18} />
                </span>

                <div>
                  <b>Withdraw Rewards</b>
                  <small>Redeem when eligible</small>
                </div>
              </div>

              <div className="hero-generation-tag">
                For every
                <br />
                generation <span>♡</span>
              </div>
            </div>
          </div>

          <div
            className="hero-benefit-grid"
            aria-label="How Ernify works"
          >
            <article className="hero-benefit-card">
              <span className="hero-benefit-icon mint">
                <Icon name="calendar" size={29} />
              </span>

              <div>
                <h2>Daily Bonus</h2>
                <p>
                  Check in daily and keep your reward
                  streak moving.
                </p>
              </div>

              <Icon
                className="hero-benefit-arrow"
                name="arrow"
                size={20}
              />
            </article>

            <article className="hero-benefit-card">
              <span className="hero-benefit-icon teal">
                <Icon name="game" size={29} />
              </span>

              <div>
                <h2>Play &amp; Earn</h2>
                <p>
                  Enjoy quick games and track your Ernify
                  Coins clearly.
                </p>
              </div>

              <Icon
                className="hero-benefit-arrow"
                name="arrow"
                size={20}
              />
            </article>

            <article className="hero-benefit-card">
              <span className="hero-benefit-icon violet">
                <Icon name="wallet" size={29} />
              </span>

              <div>
                <h2>Real Withdrawals</h2>
                <p>
                  Redeem eligible coins once you reach the
                  withdrawal threshold.
                </p>
              </div>

              <Icon
                className="hero-benefit-arrow"
                name="arrow"
                size={20}
              />
            </article>
          </div>

          <p className="hero-bottom-mantra">
            <span aria-hidden="true" />
            Small games. Real progress. A more rewarding break.
            <span aria-hidden="true" />
          </p>
        </section>

        <section
          className="conversion-strip"
          aria-label="Coin conversion and withdrawal information"
        >
          <div className="conversion-item">
            <span className="conversion-coin">₹</span>

            <strong>
              10 Ernify Coins <em>= ₹1</em>
            </strong>

            <small>
              Simple conversion, always visible in the app
            </small>
          </div>

          <div
            className="conversion-divider"
            aria-hidden="true"
          />

          <div className="conversion-item">
            <span className="conversion-icon">
              <Icon name="wallet" size={24} />
            </span>

            <strong>Withdraw from 5,000 coins</strong>

            <small>
              That equals ₹500 when your balance is eligible
            </small>
          </div>
        </section>

        <section
          className="steps-section"
          id="how-it-works"
        >
          <div className="section-heading">
            <span className="section-kicker">
              Simple by design
            </span>

            <h2>Play. Earn. Redeem.</h2>

            <p>
              Everything you need to understand your rewards
              is right inside Ernify.
            </p>
          </div>

          <div className="steps-grid">
            <article className="step-card">
              <span className="step-number">01</span>

              <span className="step-icon mint">
                <Icon name="game" size={28} />
              </span>

              <h3>Choose a game</h3>

              <p>
                Pick a game you enjoy and begin an eligible
                reward session when you are ready to play.
              </p>
            </article>

            <article className="step-card">
              <span className="step-number">02</span>

              <span className="step-icon violet">
                <Icon name="bolt" size={27} />
              </span>

              <h3>Complete a session</h3>

              <p>
                Finish an eligible game session to earn coins.
                Your daily game limit is always visible before
                you play.
              </p>
            </article>

            <article className="step-card">
              <span className="step-number">03</span>

              <span className="step-icon blue">
                <Icon name="wallet" size={27} />
              </span>

              <h3>Redeem when ready</h3>

              <p>
                Build your balance, add your bank details once
                eligible and request your payout directly in
                the app.
              </p>
            </article>
          </div>
        </section>

        <section
          className="rewards-section"
          id="rewards"
        >
          <div className="rewards-copy">
            <span className="section-kicker">
              A clearer way to earn
            </span>

            <h2>Small actions. Visible progress.</h2>

            <p>
              Ernify keeps your coins, daily progress and
              payout eligibility easy to follow, so you always
              know where you stand.
            </p>

            <ul className="check-list">
              <li>
                <Icon name="check" size={18} />
                <span>
                  <b>Daily Claim</b> — return for a reward
                  activity and grow your balance.
                </span>
              </li>

              <li>
                <Icon name="check" size={18} />
                <span>
                  <b>Weekly Bonus</b> — keep your streak moving
                  with weekly rewards.
                </span>
              </li>

              <li>
                <Icon name="check" size={18} />
                <span>
                  <b>Games</b> — see your per-game coins and
                  daily limit before you play.
                </span>
              </li>
            </ul>
          </div>

          <div className="rewards-ledger">
            <div className="ledger-top">
              <span>Today’s reward plan</span>

              <span className="ledger-status">
                <i />
                Active
              </span>
            </div>

            <div className="ledger-line">
              <span className="ledger-icon teal">
                <Icon name="calendar" size={21} />
              </span>

              <div>
                <b>Daily Claim</b>
                <small>Available in the app</small>
              </div>

              <Icon name="chevron" size={20} />
            </div>

            <div className="ledger-line">
              <span className="ledger-icon purple">
                <Icon name="gift" size={21} />
              </span>

              <div>
                <b>Weekly Bonus</b>
                <small>Keep your streak growing</small>
              </div>

              <Icon name="chevron" size={20} />
            </div>

            <div className="ledger-line">
              <span className="ledger-icon gold">
                <Icon name="game" size={21} />
              </span>

              <div>
                <b>Game Sessions</b>
                <small>Track your coins per game</small>
              </div>

              <span className="ledger-pill">
                up to 50
              </span>
            </div>

            <div className="ledger-footer">
              <Icon name="shield" size={17} />
              Rewards, availability and payouts are subject
              to the current in-app rules and account review.
            </div>
          </div>
        </section>

        <section
          className="games-section"
          id="games"
          aria-labelledby="games-heading"
        >
          <div className="games-heading-row">
            <div>
              <span className="section-kicker">
                Find your next favourite
              </span>

              <h2 id="games-heading">
                Games that make progress feel good.
              </h2>
            </div>

            <a
              className="outline-cta"
              href={playStoreUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open Ernify
              <Icon name="arrow" size={18} />
            </a>
          </div>

          <div className="games-grid">
            {games.map((game) => (
              <article
                className={`game-card ${game.accent}`}
                key={game.key}
              >
                <GameArt name={game.key} />

                <div className="game-copy">
                  <h3>{game.title}</h3>
                  <p>{game.description}</p>
                </div>

                <div className="game-earn">
                  <span className="game-coin">₹</span>

                  <span>
                    Earn up to <b>50 coins</b>
                    <small>per game / day</small>
                  </span>
                </div>

                <a
                  className="game-play"
                  href={playStoreUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Play
                  <Icon name="arrow" size={20} />
                </a>
              </article>
            ))}
          </div>
        </section>

        <section
          className="faq-section"
          id="faq"
        >
          <div className="faq-heading">
            <span className="section-kicker">
              Good to know
            </span>

            <h2>Questions, answered clearly.</h2>

            <p>
              Still need a hand? Our support team is just
              an email away.
            </p>

            <a
              href="mailto:info@ernify.in"
              className="support-link"
            >
              info@ernify.in
              <Icon name="arrow" size={18} />
            </a>
          </div>

          <div className="faq-list">
            <details open>
              <summary>
                Is Ernify free to download?
                <Icon name="chevron" size={20} />
              </summary>

              <p>
                Yes. Ernify is free to download from Google
                Play. Check the app for current reward
                availability and rules.
              </p>
            </details>

            <details>
              <summary>
                What are Ernify Coins worth?
                <Icon name="chevron" size={20} />
              </summary>

              <p>
                10 Ernify Coins equal ₹1. Your coin balance
                and the current conversion are always visible
                in the app.
              </p>
            </details>

            <details>
              <summary>
                How do game rewards work?
                <Icon name="chevron" size={20} />
              </summary>

              <p>
                Eligible game sessions add coins to your
                balance. The current per-game limit is shown
                in Ernify before you play.
              </p>
            </details>

            <details>
              <summary>
                When can I request a withdrawal?
                <Icon name="chevron" size={20} />
              </summary>

              <p>
                Cash withdrawals unlock at 5,000 coins
                (₹500). Add your bank details in Ernify once
                your balance is eligible.
              </p>
            </details>
          </div>
        </section>

        <section className="final-cta">
          <div
            className="final-orb final-orb-one"
            aria-hidden="true"
          />

          <div
            className="final-orb final-orb-two"
            aria-hidden="true"
          />

          <div>
            <span className="section-kicker">
              Ready when you are
            </span>

            <h2>
              Make your next break more rewarding.
            </h2>

            <p>
              Download Ernify, choose a game and start
              building your coin balance today.
            </p>
          </div>

          <StoreBadge className="final-badge" />
        </section>
      </main>

      <footer className="dashboard-footer">
        <div className="footer-brand">
          <img src={Logo} alt="Ernify" />

          <p>
            Play, earn and redeem — on your terms.
          </p>
        </div>

        <div className="footer-links">
          <Link to="/about-us">
            About
          </Link>

          <Link to="/privacy-policy">
            Privacy
          </Link>

          <Link to="/contact-us">
            Contact
          </Link>

          <a
            href={playStoreUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            Google Play
          </a>
        </div>

        <p className="copyright">
          © {new Date().getFullYear()} Ernify. All rights reserved.
        </p>
      </footer>
    </div>
  );
};

export default Dashboard;